import React, { useEffect, useMemo, useState, useRef, type CSSProperties } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ChevronDown, ChevronUp, Link, Zap, Folder, FileText } from 'lucide-react'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'
import { ChatPostedImages, isUserBubbleClickSuppressed } from './chat-posted-images'
import { MessageReferences } from './message-common'
import type { ChatImage, Message, DocumentReference } from '@/components/layout/chat-pane.types'
import { isPlanBuildUserText } from '@/components/layout/chat-pane.utils'
import { isUpdateWorkSpecSeedPrompt } from '@/lib/update-work-spec-prompt-display'
import { UpdateWorkSpecSeedCard } from './update-work-spec-seed-card'
import { normalizeBareUrls } from '@/lib/chat-markdown-links'
import { chatMarkdownTableComponents } from '@/components/features/shared/chat-markdown-table'
import { preserveUserPromptLineBreaks } from '@/lib/user-prompt-line-breaks'
import {
  ContentEditableChatInput,
  type ContentEditableChatInputHandle,
} from '@/components/ui/contenteditable-chat-input'
import type { SlashWorkspaceMeta } from '@/components/ui/slash-command-menu'
import {
  shouldDeferChatMessageEditEscape,
  shouldDismissChatMessageEdit,
} from '@/lib/chat-message-edit-dismiss'

/** Collapse long pastes (transcripts) so thread review stays scannable. */
const LONG_PROMPT_CHAR_THRESHOLD = 800
const LONG_PROMPT_LINE_THRESHOLD = 12

export function isLongUserPrompt(content: string | undefined): boolean {
  if (!content || content === '(image)') return false
  if (content.length >= LONG_PROMPT_CHAR_THRESHOLD) return true
  return content.split('\n').length >= LONG_PROMPT_LINE_THRESHOLD
}

function UserUrlChip({ href, children }: { href: string; children?: React.ReactNode }) {
  let display = String(children ?? href)
  try {
    const u = new URL(href)
    display = u.hostname + (u.pathname !== '/' ? u.pathname : '')
  } catch { /* use text */ }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={href}
      className="inline-flex max-w-full min-w-0 items-center gap-1 px-1.5 py-0.5 rounded-md text-xs border transition-colors bg-background/60 text-muted-foreground border-border hover:bg-muted hover:text-foreground align-middle no-underline"
    >
      <Link className="w-3 h-3 shrink-0" />
      <span className="min-w-0 truncate">{display}</span>
    </a>
  )
}

/** Shared body for live user bubbles + sticky prompt (chips must match). */
export function UserPromptMarkdownBody({
  content,
  onOpenReference,
  className,
}: {
  content: string
  onOpenReference?: (reference: DocumentReference, event: unknown) => void
  className?: string
}) {
  if (isUpdateWorkSpecSeedPrompt(content)) {
    return <UpdateWorkSpecSeedCard content={content} />
  }

  if (hasFlowNoteInlineMarkers(content)) {
    return (
      <div
        className={cn(
          'prose dark:prose-invert min-w-0 max-w-full overflow-x-hidden text-[15px] leading-relaxed [overflow-wrap:anywhere]',
          className,
        )}
      >
        {renderSegmentedUserContent(content, onOpenReference)}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'prose dark:prose-invert min-w-0 max-w-full overflow-x-hidden text-[15px] leading-relaxed [overflow-wrap:anywhere]',
        'prose-p:my-1.5 prose-p:leading-relaxed',
        'prose-pre:my-0 prose-pre:p-0 prose-pre:border-0 prose-pre:bg-transparent',
        'prose-code:before:content-none prose-code:after:content-none prose-code:bg-muted/50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono prose-code:text-foreground',
        'prose-headings:my-1.5 prose-headings:font-semibold',
        'prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5',
        'prose-strong:font-semibold prose-em:italic',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-foreground',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          ...chatMarkdownTableComponents,
          a: ({ href, children }) => (
            <UserUrlChip href={href ?? '#'}>{children}</UserUrlChip>
          ),
          code: ({ children, className: codeClassName }) => {
            const isBlock = codeClassName?.includes('language-')
            if (isBlock) {
              return <code className={codeClassName}>{children}</code>
            }
            return <code>{children}</code>
          },
          pre: ({ children }) => (
            <pre className="my-1.5 overflow-x-auto rounded-md border border-border bg-background/90 p-3 text-xs font-mono text-foreground">
              {children}
            </pre>
          ),
        }}
      >
        {normalizeBareUrls(preserveUserPromptLineBreaks(content))}
      </ReactMarkdown>
    </div>
  )
}

interface UserMessageProps {
  message: Message
  isEditing: boolean
  editContent: string
  onEditContentChange: (content: string) => void
  onStartEdit: () => void
  onCancelEdit: () => void
  /** Serialized body from the editor (includes {{FN_*}} markers when chips are inline). */
  onSaveEdit: (serializedContent: string, images?: ChatImage[]) => void | Promise<void>
  /** Same model row as the main composer (edit mode). */
  editModelBadge?: React.ReactNode
  references?: DocumentReference[]
  onInsertReference?: (reference: DocumentReference) => void
  onRemoveReference?: (id: string, ref?: DocumentReference) => void
  onOpenReference?: (reference: DocumentReference, event: unknown) => void
  openTabIds?: string[]
  recentlyViewedIds?: string[]
  currentProjectId?: string | null
  activeChatSkill?: { id: string; name: string } | null
  onActiveChatSkillChange?: (skill: { id: string; name: string } | null) => void
  /** Match main composer: scope for `/` skills and @ mentions */
  mentionFolderPaths?: string[]
  slashAllowedWorkspaceSlugs?: string[]
  slashWorkspaceMetaBySlug?: Record<string, SlashWorkspaceMeta>
  slashChatScopeLabel?: string
  slashOrganizationIds?: string[]
  /** Workspace accent for bubble border/fill/glow (from chat scope). */
  accentStyle?: CSSProperties | null
  'data-testid'?: string
}

export function hasFlowNoteInlineMarkers(text: string | undefined): boolean {
  return typeof text === 'string' && /\{\{FN_(FOLDER|DOC|SKILL):/.test(text)
}

type FlowNoteMsgSeg =
  | { t: 'text'; v: string }
  | { t: 'folder'; path: string; title: string }
  | { t: 'doc'; id: string; title: string; lines?: string }
  | { t: 'skill'; id: string; title: string }

function segmentMessageContent(content: string): FlowNoteMsgSeg[] {
  const out: FlowNoteMsgSeg[] = []
  let i = 0
  const s = content
  while (i < s.length) {
    const fi = s.indexOf('{{FN_FOLDER:', i)
    const di = s.indexOf('{{FN_DOC:', i)
    const si = s.indexOf('{{FN_SKILL:', i)
    const candidates = [fi, di, si].filter((x) => x >= 0)
    const next = candidates.length ? Math.min(...candidates) : -1
    if (next < 0) {
      if (i < s.length) out.push({ t: 'text', v: s.slice(i) })
      break
    }
    if (next > i) out.push({ t: 'text', v: s.slice(i, next) })

    const isFolder = s.startsWith('{{FN_FOLDER:', next)
    const isSkill = s.startsWith('{{FN_SKILL:', next)
    const prefix = isFolder ? '{{FN_FOLDER:' : isSkill ? '{{FN_SKILL:' : '{{FN_DOC:'
    const close = s.indexOf('}}', next + prefix.length)
    if (close < 0) {
      out.push({ t: 'text', v: s.slice(next) })
      break
    }
    const inner = s.slice(next + prefix.length, close)
    const parts = inner.split('|')
    const encLeft = parts[0] || ''
    const encRight = parts[1] || ''
    try {
      const left = decodeURIComponent(encLeft)
      const right = encRight !== '' ? decodeURIComponent(encRight) : left
      if (isFolder) out.push({ t: 'folder', path: left, title: right })
      else if (isSkill) out.push({ t: 'skill', id: left, title: right })
      else {
        const lines = /^\d+(?:-\d+)?$/.test(parts[2] || '') ? parts[2] : undefined
        out.push({ t: 'doc', id: left, title: right, ...(lines ? { lines } : {}) })
      }
    } catch {
      out.push({ t: 'text', v: s.slice(next, close + 2) })
    }
    i = close + 2
  }
  return out
}

function InlineHistoryFolderChip({
  title,
  onClick,
}: {
  title: string
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium border transition-all cursor-pointer select-none hover:scale-105 hover:shadow-sm bg-blue-500/10 text-blue-500 border-blue-500/20 align-middle mx-0.5"
    >
      <Folder className="w-3 h-3 shrink-0" aria-hidden />
      <span className="max-w-[150px] truncate">{title}</span>
    </button>
  )
}

function InlineHistoryDocChip({
  title,
  lines,
  onClick,
}: {
  title: string
  lines?: string
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium border transition-all cursor-pointer select-none hover:scale-105 hover:shadow-sm bg-gray-500/10 text-gray-500 border-gray-500/20 align-middle mx-0.5"
    >
      <FileText className="w-3 h-3 shrink-0" aria-hidden />
      <span className="max-w-[150px] truncate">{title}</span>
      {lines ? <span className="shrink-0 opacity-70">:{lines}</span> : null}
    </button>
  )
}

function InlineHistorySkillChip({ title }: { title: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-medium border bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20 align-middle mx-0.5 select-none"
      title={title}
    >
      <Zap className="w-3 h-3 shrink-0" aria-hidden />
      <span className="max-w-[150px] truncate">{title}</span>
    </span>
  )
}

const segmentedMarkdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <UserUrlChip href={href ?? '#'}>{children}</UserUrlChip>
  ),
  code: ({
    children,
    className,
  }: {
    children?: React.ReactNode
    className?: string
  }) => {
    const isBlock = className?.includes('language-')
    if (isBlock) {
      return <code className={className}>{children}</code>
    }
    return <code>{children}</code>
  },
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="my-1.5 overflow-x-auto rounded-md border border-border bg-background/90 p-3 text-xs font-mono text-foreground">
      {children}
    </pre>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <span className="inline whitespace-pre-wrap">{children}</span>
  ),
}

export function renderSegmentedUserContent(
  content: string,
  onOpenReference?: (reference: DocumentReference, event: unknown) => void,
) {
  const segments = segmentMessageContent(content)
  return (
    <div className="inline-flex flex-wrap items-baseline gap-x-1 gap-y-1 min-w-0 max-w-full">
      {segments.map((seg, idx) => {
        if (seg.t === 'text') {
          if (!seg.v) return null
          return (
            <React.Fragment key={idx}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={segmentedMarkdownComponents}>
                {normalizeBareUrls(preserveUserPromptLineBreaks(seg.v))}
              </ReactMarkdown>
            </React.Fragment>
          )
        }
        if (seg.t === 'folder') {
          return (
            <InlineHistoryFolderChip
              key={idx}
              title={seg.title}
              onClick={(e) => {
                e.stopPropagation()
                onOpenReference?.(
                  {
                    id: seg.path,
                    path: seg.path,
                    title: seg.title,
                    type: 'folder',
                    kind: 'folder',
                  },
                  e.nativeEvent,
                )
              }}
            />
          )
        }
        if (seg.t === 'skill') {
          return <InlineHistorySkillChip key={idx} title={seg.title} />
        }
        return (
          <InlineHistoryDocChip
            key={idx}
            title={seg.title}
            lines={seg.lines}
            onClick={(e) => {
              e.stopPropagation()
              onOpenReference?.(
                {
                  id: seg.id,
                  title: seg.title,
                  type: 'note',
                  kind: 'document',
                },
                e.nativeEvent,
              )
            }}
          />
        )
      })}
    </div>
  )
}

export function UserMessage({
  message,
  isEditing,
  editContent,
  onEditContentChange,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  editModelBadge,
  references = [],
  onInsertReference,
  onRemoveReference,
  onOpenReference,
  openTabIds,
  recentlyViewedIds,
  currentProjectId = null,
  activeChatSkill,
  onActiveChatSkillChange,
  mentionFolderPaths,
  slashAllowedWorkspaceSlugs,
  slashWorkspaceMetaBySlug,
  slashChatScopeLabel,
  slashOrganizationIds,
  accentStyle = null,
  'data-testid': dataTestId,
}: UserMessageProps) {
  const editInputRef = useRef<ContentEditableChatInputHandle>(null)
  const editRootRef = useRef<HTMLDivElement>(null)
  const bubbleWrapRef = useRef<HTMLDivElement>(null)
  const isUpdateSeed = isUpdateWorkSpecSeedPrompt(message.content)
  const isLong = useMemo(
    () => !isUpdateWorkSpecSeedPrompt(message.content) && isLongUserPrompt(message.content),
    [message.content],
  )
  const [expanded, setExpanded] = useState(false)
  const collapsed = isLong && !expanded

  // Reset collapse when the underlying message body changes (e.g. rewind / reload).
  useEffect(() => {
    setExpanded(false)
  }, [message.messageId, message.content])

  useEffect(() => {
    if (!isEditing) return
    const id = requestAnimationFrame(() => editInputRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [isEditing])

  // Esc and a click outside leave the edit, even after focus moved into the document.
  // Capture so a document-editor shortcut on window cannot swallow Escape first.
  // An open menu or dialog takes the first Escape.
  useEffect(() => {
    if (!isEditing) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (shouldDeferChatMessageEditEscape()) return
      e.preventDefault()
      e.stopImmediatePropagation()
      onCancelEdit()
    }
    const onPointerDown = (e: PointerEvent) => {
      if (!shouldDismissChatMessageEdit(e.target, editRootRef.current)) return
      onCancelEdit()
    }
    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [isEditing, onCancelEdit])

  const collapsePrompt = () => {
    setExpanded(false)
    requestAnimationFrame(() => {
      bubbleWrapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    })
  }

  // Plan Build is harness chrome - not an editable user prompt bubble.
  const isPlanBuildHarness =
    message.metadata?.harness === 'plan_build' || isPlanBuildUserText(message.content)
  if (!isEditing && isPlanBuildHarness) {
    return (
      <div
        className="mb-3 flex w-full max-w-full min-w-0 justify-center chat-turn-fade-in"
        data-testid={dataTestId ?? testIds.chatPane.planBuildHarness}
      >
        <div
          className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-muted-foreground"
          role="status"
          aria-label="Build from plan"
        >
          <span className="font-medium text-foreground/75">Build</span>
          <span aria-hidden="true">·</span>
          <span>Implementing plan</span>
        </div>
      </div>
    )
  }

  const onKeyDownBubble = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isUpdateSeed) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (collapsed) {
        setExpanded(true)
        return
      }
      onStartEdit()
    }
  }

  const onBubbleClick = () => {
    if (isUpdateSeed) return
    if (isUserBubbleClickSuppressed()) return
    if (document.querySelector('[data-slot="markdown-image-lightbox"]')) return
    if (collapsed) {
      setExpanded(true)
      return
    }
    onStartEdit()
  }

  const submitEdit = () => {
    const serialized =
      typeof editInputRef.current?.getSerializedMessageText === 'function'
        ? editInputRef.current.getSerializedMessageText()
        : undefined
    const images = editInputRef.current?.getImages?.() ?? message.images ?? []
    const body = (typeof serialized === 'string' ? serialized : editContent).trim()
    if (!body && images.length === 0) return
    void onSaveEdit(body || (images.length ? '(image)' : ''), images)
  }

  return (
    <div className="w-full max-w-full min-w-0 mb-3 chat-turn-fade-in">
      {isEditing ? (
        <div
          ref={editRootRef}
          className="w-full min-w-0"
          data-testid={dataTestId}
          data-chat-message-editing="true"
        >
          <ContentEditableChatInput
            ref={editInputRef}
            key={message.messageId ?? `edit-${message.content.slice(0, 24)}`}
            value={editContent === '(image)' ? '' : editContent}
            initialImages={message.images ?? []}
            onChange={onEditContentChange}
            references={references}
            onReferencesChange={() => {
              /* Parent owns list via onInsertReference / onRemoveReference */
            }}
            onInsertReference={onInsertReference}
            onRemoveReference={onRemoveReference}
            onOpenReference={onOpenReference}
            openTabIds={openTabIds}
            recentlyViewedIds={recentlyViewedIds}
            currentProjectId={currentProjectId}
            mentionFolderPaths={mentionFolderPaths}
            slashAllowedWorkspaceSlugs={slashAllowedWorkspaceSlugs}
            slashWorkspaceMetaBySlug={slashWorkspaceMetaBySlug}
            slashChatScopeLabel={slashChatScopeLabel}
            slashOrganizationIds={slashOrganizationIds}
            activeChatSkill={activeChatSkill ?? null}
            onActiveChatSkillChange={onActiveChatSkillChange}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault()
                onCancelEdit()
                return
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                submitEdit()
              }
            }}
            placeholder="Edit your message..."
            showToolbar
            onSend={submitEdit}
            modelBadge={editModelBadge}
            className="rounded-lg bg-background"
            aria-label="Edit message"
          />
        </div>
      ) : (
        <div
          ref={bubbleWrapRef}
          className="relative inline-flex w-fit max-w-[min(100%,calc(100%-1.75rem))] min-w-0 flex-col"
        >
          {/* Float near bottom of chat viewport while this expanded prompt is on screen */}
          {isLong && expanded && (
            <div
              className="sticky top-[calc(100%-1.25rem)] z-20 flex h-0 justify-center pointer-events-none"
              aria-hidden={false}
            >
              <button
                type="button"
                data-testid={testIds.chatPane.userMessageCollapseFloat}
                onClick={(e) => {
                  e.stopPropagation()
                  collapsePrompt()
                }}
                className="pointer-events-auto inline-flex -translate-y-[110%] items-center gap-1.5 rounded-full border border-border bg-background px-3.5 py-2 text-xs font-medium leading-none text-muted-foreground shadow-md backdrop-blur-sm transition-colors hover:bg-muted hover:text-foreground"
                aria-label="Collapse message"
              >
                <ChevronUp className="h-3.5 w-3.5 shrink-0" />
                Collapse
              </button>
            </div>
          )}
          <div
            data-testid={dataTestId}
            className={cn(
              'relative inline-block w-full min-w-0 rounded-2xl border px-4 py-3 text-left transition-[colors,box-shadow,background-color,background,max-height] duration-300',
              // ~12-15% of viewport - enough preview for transcripts without eating the thread
              collapsed && 'max-h-[min(15vh,12rem)] overflow-hidden',
              accentStyle
                ? ''
                : 'border-primary/20 bg-primary/10',
            )}
            style={accentStyle ?? undefined}
          >
            {/*
              Mask content (incl. chips) so it fades into the bubble fill - avoids a black
              overlay that fights accent backgrounds and lets chips punch through.
              Images sit outside the edit hit target so click-to-view cannot start rewind.
            */}
            <div
              className={cn(
                'flex flex-col gap-2',
                collapsed &&
                  '[mask-image:linear-gradient(to_bottom,black_0%,black_35%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,black_35%,transparent_100%)]',
              )}
            >
              {message.images && message.images.length > 0 && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <ChatPostedImages images={message.images} onEditMessage={onStartEdit} />
                </div>
              )}
              <div
                role={isUpdateSeed ? undefined : 'button'}
                tabIndex={isUpdateSeed ? undefined : 0}
                aria-label={
                  isUpdateSeed ? 'Update work spec' : collapsed ? 'Expand message' : 'Edit message'
                }
                aria-expanded={isLong ? expanded : undefined}
                onClick={onBubbleClick}
                onKeyDown={onKeyDownBubble}
                className={cn(
                  'min-w-0 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  isUpdateSeed ? 'cursor-default' : 'cursor-pointer',
                  !isUpdateSeed && !accentStyle && 'rounded-md hover:bg-primary/[0.06]',
                  !isUpdateSeed && accentStyle && 'rounded-md hover:brightness-[1.03]',
                )}
              >
                {message.skill && !/\{\{FN_SKILL:/.test(message.content || '') && (
                  <span className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-md text-xs font-medium border bg-violet-500/10 text-violet-600 dark:text-violet-400 border-violet-500/20">
                    <Zap className="w-3 h-3" />
                    {message.skill.name}
                  </span>
                )}
                <div className="flex flex-col gap-1 min-w-0">
                  {message.content && message.content !== '(image)' && (
                    <UserPromptMarkdownBody
                      content={message.content}
                      onOpenReference={onOpenReference}
                    />
                  )}
                  {message.editOf && (
                    <span className="text-xs text-muted-foreground/50 shrink-0 self-end pb-0.5">edited</span>
                  )}
                </div>
              </div>
            </div>
            {message.references &&
              message.references.length > 0 &&
              !hasFlowNoteInlineMarkers(message.content) && (
                <div
                  className={cn(
                    collapsed &&
                      '[mask-image:linear-gradient(to_bottom,black_0%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_0%,transparent_100%)]',
                  )}
                >
                  <MessageReferences references={message.references} />
                </div>
              )}
          </div>
          {isLong && !expanded && (
            <button
              type="button"
              data-testid={testIds.chatPane.userMessageExpand}
              onClick={(e) => {
                e.stopPropagation()
                setExpanded(true)
              }}
              className="mt-1 flex w-full items-center justify-center gap-1 rounded-md py-1.5 text-xs text-muted-foreground/70 transition-colors hover:text-muted-foreground"
              aria-label="Show full message"
            >
              <ChevronDown className="h-3.5 w-3.5" />
              <span>Show more</span>
            </button>
          )}
          {isLong && expanded && (
            <button
              type="button"
              data-testid={testIds.chatPane.userMessageExpand}
              onClick={(e) => {
                e.stopPropagation()
                collapsePrompt()
              }}
              className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-md border border-border/60 bg-muted/30 py-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label="Collapse message"
            >
              <ChevronUp className="h-3.5 w-3.5" />
              <span>Collapse</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
